/**
 * NewArrivalsPage Component
 * Displays products created within the last 30 days with sorting
 * Uses infinite scroll to load more products as user scrolls
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Button, Dropdown } from 'react-bootstrap';
import { useInfiniteNewArrivals } from '../../hooks/useProducts.js';
import { useMedRecFilter } from '../../context/MedRecFilterContext.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import ProductCard from '../../components/products/ProductCard.jsx';

const NewArrivalsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get Med/Rec filter from global context
  const { filterValues } = useMedRecFilter();

  // Get URL parameters
  const initialSort = searchParams.get('sort') || 'dateCreated';
  const initialOrder = searchParams.get('order') || 'desc';

  // Local state
  const [sortBy, setSortBy] = useState(initialSort);
  const [sortDirection, setSortDirection] = useState(initialOrder);

  // Infinite scroll sentinel ref
  const sentinelRef = useRef(null);

  // Get new arrivals with infinite scroll (always in-stock only, filtered server-side)
  const {
    products,
    pagination,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteNewArrivals({
    pageSize: 24,
    sortBy,
    sortDirection,
    isMed: filterValues.isMed,
    isRec: filterValues.isRec,
  });

  // Intersection Observer for infinite scroll
  const handleObserver = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(handleObserver, {
      rootMargin: '600px',
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleObserver]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (sortBy !== 'dateCreated') params.set('sort', sortBy);
    if (sortDirection !== 'desc') params.set('order', sortDirection);

    setSearchParams(params);
  }, [sortBy, sortDirection, setSearchParams]);

  // Handle sort change
  const handleSortChange = (newSortBy, newDirection = 'desc') => {
    setSortBy(newSortBy);
    setSortDirection(newDirection);
  };

  return (
    <div className="bg-light min-vh-100">
      {/* Page Header */}
      <section className="bg-white py-5 border-bottom">
        <Container fluid className="">
          <Row className="align-items-center">
            <Col md={8}>
              {/* Breadcrumb */}
              <nav aria-label="breadcrumb" className="mb-3">
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="/" className="text-decoration-none">Home</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    New Arrivals
                  </li>
                </ol>
              </nav>

              {/* Page Info */}
              <div className="d-flex align-items-center mb-2">
                <div>
                  <h1 className="h2 mb-0">
                    New Arrivals
                  </h1>
                  <p className="text-muted mb-0">
                    {pagination.totalItems || 0} products added in the last 30 days
                  </p>
                </div>
              </div>
            </Col>

            <Col md={4} className="text-md-end">
              <Link to="/products" className="btn btn-outline-primary">
                <i className="bi bi-grid me-1"></i>
                View All Products
              </Link>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Sort Controls */}
      <section className="bg-white py-3 border-bottom">
        <Container fluid className="">
          <Row className="align-items-center">
            <Col className="d-flex align-items-center justify-content-md-end">
              <span className="me-3 text-muted">Sort by:</span>

              <Dropdown align="end">
                <Dropdown.Toggle variant="outline-secondary" size="sm">
                  {sortBy === 'dateCreated' && 'Newest'}
                  {sortBy === 'productName' && 'Name'}
                  <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleSortChange('dateCreated', 'desc')}>
                    Newest First
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortChange('dateCreated', 'asc')}>
                    Oldest First
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => handleSortChange('productName', 'asc')}>
                    Name (A-Z)
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSortChange('productName', 'desc')}>
                    Name (Z-A)
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Products Grid */}
      <section className="py-4">
        <Container fluid className="">
          {isLoading ? (
            <div className="text-center py-5">
              <LoadingSpinner />
              <p className="mt-3 text-muted">Loading new arrivals...</p>
            </div>
          ) : error ? (
            <div className="text-center py-5">
              <div className="alert alert-warning">
                <h5>Error Loading Products</h5>
                <p>{error.message}</p>
                <Button variant="primary" onClick={() => refetch()}>
                  Try Again
                </Button>
              </div>
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="product-grid">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} className="text-center py-4">
                {isFetchingNextPage && (
                  <>
                    <LoadingSpinner />
                    <p className="mt-2 text-muted">Loading more products...</p>
                  </>
                )}
                {!hasNextPage && products.length > 24 && (
                  <small className="text-muted">
                    Showing all {pagination.totalItems} products
                  </small>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <i className="bi bi-box-seam display-1 text-muted mb-3"></i>
              <h3 className="text-muted">No New Arrivals</h3>
              <p className="text-muted">
                No products have been added in the last 30 days.
              </p>
            </div>
          )}
        </Container>
      </section>
    </div>
  );
};

export default NewArrivalsPage;